import { FieldErrors } from 'common/components/core/FieldErrors'
import React, { useEffect, useRef, useState } from 'react'
import { PublicPageFieldSlugEnum } from 'src/public-pages/enums/public-page-field-slug-enum'
import { useRegisterConfirmSubmit } from 'src/public-pages/hooks/use-register-confirm-submit'
import { WebsitePublicLogo } from 'src/public-pages/shared/components/layout/website-public-logo'
import { PublicPageButton } from 'src/public-pages/shared/components/public-page-button'
import { PublicPageField } from 'src/public-pages/shared/components/public-page-field'
import { PublicPageHeading } from 'src/public-pages/shared/components/public-page-heading'
import { PublicPageText } from 'src/public-pages/shared/components/public-page-text'
import {
  Recaptcha,
  RecaptchaHandleInterface,
} from 'src/public-pages/shared/components/recaptha/recaptcha'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import {
  WebsiteButtonsInterface,
  WebsiteFormFieldsSettings,
  WebsiteThemeInterface,
  WebsiteTypographySettingsStylesInterface,
} from 'src/shared/types/website-settings-types'

export interface RegisterConfirmContentProps {
  token: string
  logoUrl?: string
  isCustomDomain?: boolean
  heading?: WebsiteTypographySettingsStylesInterface
  paragraph?: WebsiteTypographySettingsStylesInterface
  theme?: WebsiteThemeInterface
  formFields?: Partial<WebsiteFormFieldsSettings>
  button?: Partial<WebsiteButtonsInterface>
}

export const RegisterConfirmContent: React.FC<RegisterConfirmContentProps> = ({
  token,
  logoUrl,
  isCustomDomain,
  heading,
  theme,
  formFields,
  paragraph,
  button,
}) => {
  const { t } = useTypedTranslation()
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    plainPassword,
    setPlainPassword,
    confirmPlainPassword,
    setConfirmPlainPassword,
    handleSubmit,
    loading,
    errors,
  } = useRegisterConfirmSubmit(token)
  const [captchaToken, setCaptchaToken] = useState('')
  const recaptchaRef = useRef<RecaptchaHandleInterface>(null)

  useEffect(() => {
    if (errors?.root) {
      recaptchaRef.current?.reset()
    }
  }, [errors?.root])

  return (
    <div>
      <WebsitePublicLogo
        logoUrl={logoUrl}
        isCustomDomain={isCustomDomain}
        alt="Logo"
      />
      <PublicPageHeading
        heading={heading}
        theme={theme}
        style={{ marginBottom: '12px' }}
      >
        {t('website_public_page.register_confirm.heading')}
      </PublicPageHeading>
      <PublicPageText
        paragraph={paragraph}
        theme={theme}
        style={{ marginBottom: '40px' }}
        textAlign="center"
      >
        {t('website_public_page.register_confirm.sub_heading')}
      </PublicPageText>

      <div>
        <div style={{ marginBottom: '16px' }}>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '10px' }}
            requiredField
          >
            {t('website_public_page.register_confirm.first_name.label')}
          </PublicPageText>

          <PublicPageField
            type={PublicPageFieldSlugEnum.FirstName}
            name={PublicPageFieldSlugEnum.FirstName}
            formFields={formFields}
            theme={theme}
            value={firstName}
            onChange={e => setFirstName(e.target.value)}
          />
          <FieldErrors
            errors={errors?.[PublicPageFieldSlugEnum.FirstName]}
            fontFamily={paragraph?.fontFamily}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '10px' }}
            requiredField
          >
            {t('website_public_page.register_confirm.last_name.label')}
          </PublicPageText>

          <PublicPageField
            type={PublicPageFieldSlugEnum.LastName}
            name={PublicPageFieldSlugEnum.LastName}
            formFields={formFields}
            theme={theme}
            value={lastName}
            onChange={e => setLastName(e.target.value)}
          />
          <FieldErrors
            errors={errors?.[PublicPageFieldSlugEnum.LastName]}
            fontFamily={paragraph?.fontFamily}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '10px' }}
            requiredField
          >
            {t('website_public_page.register_confirm.password.label')}
          </PublicPageText>

          <PublicPageField
            type={PublicPageFieldSlugEnum.Password}
            name={PublicPageFieldSlugEnum.Password}
            formFields={formFields}
            theme={theme}
            value={plainPassword}
            onChange={e => setPlainPassword(e.target.value)}
          />
          <FieldErrors
            errors={errors?.[PublicPageFieldSlugEnum.Password]}
            fontFamily={paragraph?.fontFamily}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '10px' }}
            requiredField
          >
            {t('website_public_page.register_confirm.confirm_password.label')}
          </PublicPageText>

          <PublicPageField
            type={PublicPageFieldSlugEnum.Password}
            name={PublicPageFieldSlugEnum.ConfirmPassword}
            formFields={formFields}
            theme={theme}
            value={confirmPlainPassword}
            onChange={e => setConfirmPlainPassword(e.target.value)}
          />
          <FieldErrors
            errors={errors?.[PublicPageFieldSlugEnum.ConfirmPassword]}
            fontFamily={paragraph?.fontFamily}
          />
          <FieldErrors
            errors={errors?.root}
            fontFamily={paragraph?.fontFamily}
          />
        </div>

        <div style={{ marginBottom: '36px' }}>
          <Recaptcha
            ref={recaptchaRef}
            onTokenChange={setCaptchaToken}
            onError={() => setCaptchaToken('')}
          />
        </div>

        <PublicPageButton
          button={button}
          theme={theme}
          text={t('website_public_page.register_confirm.submit_button_text')}
          loading={loading}
          onClick={() => handleSubmit(captchaToken)}
          width="100%"
          paragraph={paragraph}
        />
      </div>
    </div>
  )
}
